import registry from "./experienceTelemetryRegistry.json";

export const EXPERIENCE_TELEMETRY_CONTRACT_VERSION = Number(
  registry.contractVersion || 1,
);

export const EXPERIENCE_EVENT_NAMES = new Set(registry.eventNames || []);
export const EXPERIENCE_MEMBER_STAGES = new Set(registry.memberStages || []);
export const EXPERIENCE_ACTION_DIMENSION_VALUES = Object.freeze(
  registry.actionDimensionValues || {},
);

export const EXPERIENCE_TELEMETRY_REGISTRY = Object.freeze(
  registry.experiences || {},
);

const STEP_EVENT_NAMES = new Set([
  "experience_step_viewed",
  "experience_step_dwell_summary",
  "experience_step_advanced",
  "experience_step_back",
  "experience_step_skipped",
  "experience_validation_failed",
]);

const normalizeKey = (value = "") => String(value || "").trim();

export const normalizeExperienceActionDimensionValue = (value) =>
  String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

const hasValidActionDimensions = ({ definition, actionId, properties }) =>
  Object.entries(definition.actionDimensions?.[actionId] || {}).every(
    ([propertyKey, valueSetKey]) => {
      const value = properties?.[propertyKey];
      if (value === undefined || value === null || value === "") return true;
      return (EXPERIENCE_ACTION_DIMENSION_VALUES[valueSetKey] || [])
        .map(String)
        .includes(normalizeExperienceActionDimensionValue(value));
    },
  );

export const getExperienceDefinition = (experienceKey = "") =>
  EXPERIENCE_TELEMETRY_REGISTRY[normalizeKey(experienceKey)] || null;

export const isExperienceEventName = (eventName = "") =>
  EXPERIENCE_EVENT_NAMES.has(normalizeKey(eventName));

export const validateExperienceTelemetryEvent = ({
  experienceKey = "",
  eventName = "",
  flow = "",
  surface = "",
  properties = {},
} = {}) => {
  const definition = getExperienceDefinition(experienceKey);
  if (!definition) return { ok: false, reason: "unknown-experience" };
  if (!isExperienceEventName(eventName)) {
    return { ok: false, reason: "unsupported-experience-event" };
  }
  if (normalizeKey(flow) !== definition.flow) {
    return { ok: false, reason: "experience-flow-mismatch" };
  }
  if (normalizeKey(surface) !== definition.surface) {
    return { ok: false, reason: "experience-surface-mismatch" };
  }
  if (
    normalizeKey(properties?.telemetryContract) !==
    `experience_v${EXPERIENCE_TELEMETRY_CONTRACT_VERSION}`
  ) {
    return { ok: false, reason: "experience-contract-mismatch" };
  }
  if (!normalizeKey(properties?.flowInstanceId)) {
    return { ok: false, reason: "missing-experience-flow-instance" };
  }
  if (!EXPERIENCE_MEMBER_STAGES.has(normalizeKey(properties?.memberStage))) {
    return { ok: false, reason: "unsupported-experience-member-stage" };
  }
  if (
    (properties?.firstVisit !== undefined &&
      typeof properties.firstVisit !== "boolean") ||
    (properties?.skipped !== undefined &&
      typeof properties.skipped !== "boolean") ||
    (properties?.isResume !== undefined &&
      typeof properties.isResume !== "boolean")
  ) {
    return { ok: false, reason: "invalid-experience-boolean" };
  }
  const entrySource = normalizeKey(properties?.entrySource);
  if (!entrySource || !definition.entrySources.includes(entrySource)) {
    return { ok: false, reason: "unsupported-experience-entry-source" };
  }

  const stepIds = [
    properties?.stepId,
    properties?.fromStepId,
    properties?.toStepId,
  ]
    .map(normalizeKey)
    .filter(Boolean);
  if (stepIds.some((stepId) => !definition.steps.includes(stepId))) {
    return { ok: false, reason: "unsupported-experience-step" };
  }
  if (STEP_EVENT_NAMES.has(eventName) && !stepIds.length) {
    return { ok: false, reason: "unsupported-experience-step" };
  }

  if (eventName === "experience_action_taken") {
    const actionId = normalizeKey(properties?.actionId);
    if (!definition.actions.includes(actionId)) {
      return { ok: false, reason: "unsupported-experience-action" };
    }
    if (!hasValidActionDimensions({ definition, actionId, properties })) {
      return { ok: false, reason: "unsupported-experience-action-dimension" };
    }
  }

  const cardId = normalizeKey(properties?.cardId);
  if (cardId && !definition.cards.includes(cardId)) {
    return { ok: false, reason: "unsupported-experience-card" };
  }
  if (eventName === "experience_card_viewed" && !cardId) {
    return { ok: false, reason: "unsupported-experience-card" };
  }
  if (
    eventName === "experience_flow_closed" &&
    !definition.closeReasons.includes(normalizeKey(properties?.closeReason))
  ) {
    return { ok: false, reason: "unsupported-experience-close-reason" };
  }
  if (
    eventName === "experience_validation_failed" &&
    !definition.validationReasons.includes(normalizeKey(properties?.reasonCode))
  ) {
    return { ok: false, reason: "unsupported-experience-validation-reason" };
  }
  if (
    eventName === "experience_flow_completed" &&
    !definition.outcomes.includes(normalizeKey(properties?.outcome))
  ) {
    return { ok: false, reason: "unsupported-experience-outcome" };
  }

  return { ok: true, definition };
};
